import pxToRem from "hooks/usePxToRem";
import {useExternalContacts, useResponsibleNextContacts} from "./hooks";

import {Stack} from "@mui/material";

import {CustomAccordion, CustomButton, CustomIconButton} from "ui";
import ProcessContactTable from "./ProcessContactTable";
import {ConfirmModal, DefaultCardStructure} from "components";
import {useFormContext} from "react-hook-form";
import {
  ExternalContactData,
  ResponsibleNextContactData,
} from "./Contacts.types";
import {useState} from "react";
import {ModalLinkContactToProcess} from "./components";
import {
  DeleteMovimentationContacts,
  DeleteResponsibles,
} from "services/api_v2/Contacts/Contacts.service";
import useToast from "hooks/useToast";
import useAlertMessage from "hooks/useAlertMessage";
import {AxiosError} from "axios";
import {Responsible} from "views/ProcessDetails/refactor/types/responsibles.types";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {ExternalContact} from "views/ProcessDetails/refactor/types/groupActivities.types";

type Props = {
  refetchProcess: () => Promise<Process | undefined>;
};

const Contacts = (props: Props) => {
  const {columnsData} = useExternalContacts();
  const {responsibleNextContactsTableColumnsData} =
    useResponsibleNextContacts();
  const [showAlert] = useAlertMessage();
  const [showToast] = useToast();

  const {getValues} = useFormContext<Process>();
  const cancelledProcess = getValues("agencyingSituation") === "Cancelado";

  const mapDataToResponsiblesTable = (
    responsibles: Responsible[],
  ): ResponsibleNextContactData[] => {
    const result: ResponsibleNextContactData[] = responsibles?.map(
      (responsible) => ({
        id: responsible.id.toString(),
        name: responsible.employee.person.name,
        email: responsible.employee.person.email,
        phone: responsible.employee.person.phoneNumber,
        role: responsible.taskRole?.name ?? "-",
      }),
    );

    return result;
  };

  const mapDataToExternalContactsTable = (
    externalContacts: ExternalContact[],
  ): ExternalContactData[] => {
    const result: ExternalContactData[] = externalContacts?.map(
      (externalContact) => ({
        id: externalContact.id.toString(),
        name: externalContact.contact.personContact.name ?? "-",
        email: externalContact.contact.personContact.email ?? "-",
        phoneNumber: externalContact.contact.personContact.phoneNumber ?? "-",
        category: externalContact.contact.category ?? "-",
        messageGroups: externalContact.movimentationContactGroup.map(
          (movimentationContactGroup) => ({
            id: movimentationContactGroup.id,
            idMovimentationContact:
              movimentationContactGroup.idMovimentationContact,
            idSendMessageGroup: movimentationContactGroup.idSendMessageGroup,
            sendMessageGroup: {
              id: movimentationContactGroup.sendMessageGroup.id,
              groupName:
                movimentationContactGroup.sendMessageGroup.groupName ?? "-",
              isActive: movimentationContactGroup.sendMessageGroup.isActive,
            },
          }),
        ),
      }),
    );

    return result;
  };

  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [responsiblesTableData, setResponsiblesTableData] = useState(
    mapDataToResponsiblesTable(getValues("responsibles")),
  );
  const [contactsTableData, setContactsTableData] = useState(
    mapDataToExternalContactsTable(getValues("groupActivity.externalContacts")),
  );

  const [responsibleToDeleteId] = useState<number>();
  const [contactToDeleteId, setContactToDeleteId] = useState<number>();
  const [objectToDelete, setObjectToDelete] = useState<
    "responsible" | "externaContact" | null
  >(null);

  const handleDeleteExternalContact = (contact: ExternalContactData) => {
    setContactToDeleteId(+contact.id);
    setObjectToDelete("externaContact");
    setConfirmModalIsOpen(true);
  };

  const deleteResponsible = async () => {
    try {
      if (objectToDelete === "externaContact" && contactToDeleteId) {
        await DeleteMovimentationContacts(contactToDeleteId);
      }

      if (objectToDelete === "responsible" && responsibleToDeleteId) {
        await DeleteResponsibles(responsibleToDeleteId);
      }
      const result = await props.refetchProcess();

      const responsiblesTableData = mapDataToResponsiblesTable(
        result?.responsibles ?? [],
      );

      const contactsTableData = mapDataToExternalContactsTable(
        result?.groupActivity?.externalContacts ?? [],
      );

      setResponsiblesTableData(responsiblesTableData);
      setContactsTableData(contactsTableData);
      setConfirmModalIsOpen(false);
      showToast("Contato excluído com sucesso!", "success");
    } catch (err) {
      const error = err as AxiosError;
      showAlert(error?.response?.data?.message);
      console.error(error);
    }
  };

  const handleConfirmDeleteResponsible = async () => {
    await deleteResponsible();
  };

  const [modalToLinkContactIsOpen, setModalToLinkContactIsOpen] =
    useState(false);

  const handleOpenModal = () => {
    setModalToLinkContactIsOpen(true);
  };

  const handleCloseModal = async (refetch = false) => {
    setModalToLinkContactIsOpen(false);
    if (refetch) {
      const result = await props.refetchProcess();
      const responsiblesTableData = mapDataToResponsiblesTable(
        result?.responsibles ?? [],
      );
      const contactsTableData = mapDataToExternalContactsTable(
        result?.groupActivity?.externalContacts ?? [],
      );
      setResponsiblesTableData(responsiblesTableData);
      setContactsTableData(contactsTableData);
    }
  };

  return (
    <>
      {modalToLinkContactIsOpen && (
        <ModalLinkContactToProcess
          modalIsOpen={modalToLinkContactIsOpen}
          handleCloseModal={handleCloseModal}
        />
      )}
      {confirmModalIsOpen && (
        <ConfirmModal
          isOpen={confirmModalIsOpen}
          title="Remover contato do processo"
          subtitle="Essa ação não pode ser desfeita!"
          confirmButtonFn={() => handleConfirmDeleteResponsible()}
          onClose={() => setConfirmModalIsOpen(false)}
        />
      )}
      <DefaultCardStructure
        title="Contatos do processo"
        isSubcard
        hasDivider={false}
        button={
          <CustomButton onClickFn={handleOpenModal} disabled={cancelledProcess}>
            Incluir Contato
          </CustomButton>
        }
      >
        <Stack px={pxToRem(1)}>
          <CustomAccordion
            title="Responsáveis Next"
            titleStyle={{fontSize: pxToRem(16), fontWeight: 400}}
          >
            <ProcessContactTable
              columns={responsibleNextContactsTableColumnsData}
              data={
                responsiblesTableData as unknown as ResponsibleNextContactData[]
              }
              // Inclusão de Responsáveis Next não será habilitado neste momento.
              //enableRowActions
              // renderRowActions={({row}) => (
              //   <CustomIconButton
              //     color="error"
              //     size="small"
              //     iconName="delete"
              //     tooltipText="Excluir responsável Next"
              //     onClick={() => handleDeleteResponsible(row.original)}
              //   />
              // )}
            />
          </CustomAccordion>

          <CustomAccordion
            title="Contatos externos"
            titleStyle={{fontSize: pxToRem(16), fontWeight: 400}}
          >
            <ProcessContactTable
              columns={columnsData}
              data={contactsTableData as unknown as ExternalContactData[]}
              enableRowActions
              renderRowActions={({row}) => (
                <CustomIconButton
                  disabled={cancelledProcess}
                  color="error"
                  size="small"
                  iconName="delete"
                  tooltipText="Excluir contato externo"
                  onClick={() => handleDeleteExternalContact(row.original)}
                />
              )}
            />
          </CustomAccordion>
        </Stack>
      </DefaultCardStructure>
    </>
  );
};

export default Contacts;
